import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Beer, QrCode } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RedeemDrinkButton({ className = '' }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLookup = async (e) => {
    e?.preventDefault?.();
    const clean = code.trim().toUpperCase();
    if (clean.length < 4) {
      toast.error('Enter the full ticket code');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/drink-tickets/by-code/${clean}`);
      setOpen(false);
      setCode('');
      navigate(`/tickets/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ticket not found');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className={className}
        data-testid="open-redeem-drink-btn"
      >
        <Beer className="w-4 h-4 mr-2 text-amber-300" />
        Redeem Drink Ticket
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Beer className="w-5 h-5 text-amber-300" />
              Redeem a Drink Ticket
            </DialogTitle>
            <DialogDescription>
              Scan the player's QR code, or type the 8-character code from their ticket.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLookup} className="space-y-4">
            <div>
              <Label className="text-white text-sm" htmlFor="redeem-code">Ticket code</Label>
              <Input
                id="redeem-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. AB12CD34"
                className="bg-muted border-white/10 text-white font-mono tracking-widest text-lg text-center"
                data-testid="redeem-code-input"
                autoFocus
                required
                maxLength={12}
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-secondary text-black hover:bg-secondary/90"
              data-testid="redeem-lookup-btn"
            >
              {loading ? 'Looking up…' : 'Find ticket'}
            </Button>
            <div className="border-t border-white/5 pt-3 flex items-center gap-2 text-xs text-muted-foreground justify-center">
              <QrCode className="w-3.5 h-3.5" />
              Tip: on mobile, use your camera to scan the QR — it opens the ticket directly.
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default RedeemDrinkButton;
